import React, { useEffect, useState } from 'react';
import { Image, Modal, StyleSheet, TouchableOpacity, TouchableWithoutFeedback } from "react-native"
import type { AthleteProps, EventProps, ExpirationProps, LocationProps, MarketProps, MatchProps, MyPlayerProps, OrderProps, PlayerBalanceProps, TeamProps, TournamentProps } from "../../../src/types"
import { Button, Text, TextInput, View } from "../../../src/Components/Themed"
import { MarketAppHelpers } from '../../market/helpers';
import { MarketComponentApi, MarketComponentHelpers } from '../../../src/MarketComponents/api';
import { useColors } from '../../../src/constants/useColors';
import { Icons, Switch } from '../../../src/Components';
import type { Moment } from 'moment-mini';
import moment from 'moment-mini';
import { BEPostHog } from 'be-components';
import FreeBonusBanner from './FreeBonusBanner';

type OrderHolderProps = {
    company_id?:string,
    visible:boolean,
    markets:MarketProps[],
    location?: {
        location: LocationProps,
        region?:string,
        coordinates?: { latitude:number, longitude:number, [key:string]:number }
    },
    event?:EventProps,
    tournament?:TournamentProps,
    match?:MatchProps,
    me?:MyPlayerProps,
    my_balance?:PlayerBalanceProps,
    order:OrderProps,
    mode: 'desktop'|'mobile',
    onClose:(data?:{ view_order:boolean }) => void,
    onCreatedOrder:(order:OrderProps) => void,
    onRequestLocation:() => void,
    onRequestAuthenticate:() => void,
    onShowProfile?:() => void

}

const OrderHolder = ({ visible, company_id, event, tournament, match, location, markets, me, my_balance, order, mode, onRequestLocation, onClose, onCreatedOrder, onRequestAuthenticate, onShowProfile }:OrderHolderProps) => {
    const Colors = useColors();
    const [ created_order, setCreatedOrder ] = useState<OrderProps | undefined>(undefined);
    const [ reviewed, setReviewed ] = useState(false);
    const [ order_started, setOrderStarted ] = useState<Moment | undefined>(undefined)
    const [ bet_loading, setBetLoading ] = useState(false);
    const [ expire_option, setExpireOption ] = useState<ExpirationProps|undefined>(undefined);
    const [ draft_data, setDraftData ] = useState<{
        open_amt: string,
        market_type: 'FOR_MONEY'|'FREE',
        order_type: 'market',
        promo:boolean
    }>({
        open_amt: '10',
        market_type: 'FOR_MONEY',
        order_type: 'market',
        promo: false
    });
    const { open_amt, promo, market_type, order_type } = draft_data;
    const market = markets.find(m => m.market_id == order.market_id);
    
    const commission_pct = location?.location?.commission_pct ?? 0.015
    const { int_open_amt, potential_winnings, estimated_earnings } = MarketAppHelpers.getDraftOrderData(order, open_amt, commission_pct);
    const cl = promo ? 'P' : market_type == 'FOR_MONEY' ? '$' : 'E'
    
    const is_valid = MarketAppHelpers.isOrderValid({ ...order, open_amt:int_open_amt, potential_winnings: potential_winnings }, order_type, market_type, order.title, market, my_balance, location?.location, event, tournament, match, expire_option)
    const quick_options = MarketAppHelpers.getQuickExpireOptions(order.event_type, event, tournament, match)
    useEffect(() => {
        setCreatedOrder(undefined);
        setReviewed(false);
        let game_start_option = quick_options.find(o => o.default)
        if(game_start_option){ setExpireOption(game_start_option) }
        else { setExpireOption(quick_options[0]) }
        setOrderStarted(moment());
        if(order){
            BEPostHog.EventActions.captureEvent('draft_order', { company_id, source:'embed', source_type:'market', location: window?.location?.href, order_title: order.title })
        }
    },[JSON.stringify(order)]);

    useEffect(() => {
        if(!me || !my_balance){ return }
        if(me.vouched_status == 'verified'){ 
            setDraftData({ ...draft_data, market_type: 'FOR_MONEY' })
        } else {
            if(my_balance.promo_balance > 0){ setDraftData({ ...draft_data, market_type: 'FOR_MONEY', promo: true, open_amt: my_balance.promo_balance.toFixed() }) }
            else { setDraftData({ ...draft_data, market_type: 'FREE' }) }
        }
    },[my_balance]);

    const handleSubmitOrder = async() => {
        if(!reviewed){ return }
        if(!order_started){ 
            alert('Sorry! We are not able to process this order') 
            return onClose()
        }
        let diff = moment().diff(order_started, 'seconds');
        if(diff > 120){
            alert('Sorry! This order has expired.  Please try again') 
            return onClose()
        }
        if(!order || !location?.location || !location.coordinates){ return }
        if(bet_loading){ return }//No mashy mashy
        if(is_valid.length > 0){ return } //We have errors!
        let new_order_type:'limit'|'market' = order_type == 'market' ? 'market' : 'limit'
        const expire_datetime = MarketAppHelpers.getExpireDatetimeFromOption(expire_option);
        if(!location.region){ return }
        if(!location.coordinates){ return }
        let draft_order:OrderProps = {
            ...order,
            open_amt:int_open_amt,
            called_amt: 0,
            buy_sell_ind: 'buy',
            promo_balance: promo,
            live: MarketAppHelpers.isLive(order.event_type, event, tournament, match),
            order_type: new_order_type,
            market_type: market_type,
            collar_pct: 0.02,
            location: location.coordinates,
            region: location.region,
            potential_winnings,
            expire_datetime
        }
        setBetLoading(true);
        const saved_order = await MarketComponentApi.createOrder(draft_order);
        setBetLoading(false)
        if(!saved_order){ 
            alert('Unable to process order!');
            return onClose()
        }
        onCreatedOrder(order);
        setCreatedOrder(order);
    }

    const renderAvailableAmount = () => {
        if(!my_balance){ return <></> }
        if(market_type == 'FREE'){
            return (
                <Text theme='success' style={{ marginTop:3 }}>{cl}{my_balance.free_market_balance.toFixed(2)} Available</Text>
            )
        }
        if(promo){
            return (
                <Text theme={my_balance.promo_balance > 0 ? 'success' : 'warning'} style={{ marginTop:3 }}>{cl}{my_balance.promo_balance.toFixed(2)} Available</Text>
            )
        }
        return (
            <Text theme={my_balance.balance > 0 ? 'success' : 'warning'} style={{ marginTop:3 }}>{cl}{my_balance.balance.toFixed(2)} Available</Text>
        )

    }

    const disabled = created_order || bet_loading || is_valid.length > 0 ? true : false

    return (
        <Modal
            style={styles.modalContainer}
            animationType={mode=='desktop' ? 'fade' : 'slide'}
            transparent
            
            visible={visible}
        >
            <TouchableWithoutFeedback style={{ flex:1 }} onPress={() => onClose()}>
                <View type="blur" style={mode=='desktop' ? styles.desktopContainer : { ...styles.viewContainer, padding:10}}>
                    <TouchableWithoutFeedback>
                    <View transparent style={mode=='desktop' ?{ width:500,maxHeight:750, borderRadius:22 }:{  }}>
                        <View float style={{ flex:1 }}>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                                <View transparent style={{ flex:1 }}>
                                    <Text theme='h1'>{order.title}</Text>
                                    <Text theme='description' style={{ marginTop:4 }}>{MarketComponentHelpers.getOddsLabel(order.odds)} ({(order.probability * 100).toFixed(2)}%)</Text>
                                </View>
                                {me ?
                                <TouchableOpacity onPress={() => onShowProfile && onShowProfile()} activeOpacity={0.7}>
                                    <Image
                                        source={{ uri: me.profile_pic && me.profile_pic != '' ? me.profile_pic : 'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                                        style={{ height:40, width:40, borderRadius:100 }}
                                        resizeMode='cover'
                                    />
                                </TouchableOpacity>
                                :<></>}
                            </View>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopWidth:1, borderColor:Colors.borders.light }}>
                                <View transparent style={{ flex:1 }}>
                                    <Text theme='h2'>Currency: {market_type == 'FOR_MONEY' ? 'USD / Promo' : 'Edge Coins'}</Text>
                                    <Text theme='description' style={{ marginTop:3 }}>Use the toggle to switch to {market_type == 'FREE' ? 'USD' : 'Edge Coins'}</Text>
                                </View>
                                <Switch
                                    value={market_type == 'FOR_MONEY' ? true : false}
                                    onChange={(val) => val ? setDraftData({ ...draft_data, market_type: 'FOR_MONEY' }) : setDraftData({ ...draft_data, market_type: 'FREE' })}
                                    switch_type='on_off'
                                />
                            </View>
                            <View type='body' style={{ flex:1 }}>
                                <View type='row' style={{ padding:20 }}>
                                    <View style={{ flex:1 }}>
                                        <Text theme='h1'>Amount?</Text>
                                        <Text theme='description' style={{ marginTop:3 }}>How much would you like to bet?</Text>
                                    </View>
                                    <TextInput
                                        value={open_amt}
                                        style={{ borderRadius:8, textAlign:'center', marginLeft:5 }}
                                        onChangeText={(text) => {
                                            setDraftData({ ...draft_data, open_amt: text })
                                            if(reviewed){ setReviewed(false) }
                                        }}
                                    />
                                </View>
                            </View>
                            <View type='row' style={{ padding:10, backgroundColor:Colors.views.footer, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                                <View transparent style={{ flex:1 }}>
                                    <Text theme='h1'>{cl}{int_open_amt.toFixed(2)}</Text>
                                    {renderAvailableAmount()}
                                </View>
                                <View transparent>
                                    <Text theme='h1' textAlign='center'>To Win</Text>
                                    <Text theme='description' style={{ marginTop:3 }} textAlign='center'>{MarketComponentHelpers.getOddsLabel(order.odds)}</Text>
                                </View>
                                    <View transparent style={{ flex:1, alignItems:'flex-end' }}>
                                    <Text theme='h1'>{cl}{potential_winnings.toFixed(2)}</Text>
                                    <Text theme='warning' style={{ marginTop:3 }}>Est Earnings: {cl}{estimated_earnings.toFixed(2)}</Text>
                                </View>
                            </View>
                            {me && !created_order ?
                            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                                {reviewed ?
                                <Button
                                    title={`Confirm ${cl}${int_open_amt.toFixed(2)}`}
                                    type='success'
                                    loading={bet_loading}
                                    style={{ flex:1, opacity:disabled ? 0.5: 1 }}
                                    disabled={disabled}
                                    onPress={() => handleSubmitOrder()}
                                />
                                :
                                <View transparent style={{ flex:1, flexDirection:'row' }}>
                                    {!location?.location ?
                                    <Button
                                        title='ENABLE LOCATION'
                                        style={{ flex:1, marginRight:5 }}
                                        backgroundColor={Colors.text.warning}
                                        title_color={Colors.text.white}
                                        onPress={() => onRequestLocation()}
                                    />
                                    :<></>}
                                    <Button
                                        title='REVIEW'
                                        backgroundColor={Colors.absolutes.brand.electric}
                                        title_color={Colors.text.white}
                                        loading={bet_loading}
                                        style={{ flex:1, opacity:disabled ? 0.5: 1 }}
                                        disabled={disabled}
                                        onPress={() => {
                                            setBetLoading(true);
                                            setTimeout(() => {
                                                setBetLoading(false);
                                                setReviewed(true);
                                            }, 500);
                                        }}
                                    />
                                </View>
                                }
                            </View>
                            : me && created_order ?
                             <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                                <Button
                                    type='close'
                                    style={{ marginRight:4, flex:1 }}
                                    title='Close'
                                    onPress={() => onClose()}
                                />
                                <Button type='success' style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', flex:2, marginLeft:4 }} onPress={() => onClose({ view_order: true })}>
                                    <Icons.OrderIcon size={14} color={Colors.text.white} />
                                    <Text style={{ marginRight:4 }} theme='h1' color={Colors.text.white}>View My Bet!</Text>
                                </Button>
                            </View>
                            :
                            <>
                                {!me && (
                                    <FreeBonusBanner
                                        amount="$10"
                                        message="FREE to place your first bet!"
                                        ctaText="Claim Now"
                                        onPress={onRequestAuthenticate}
                                    />
                                )}
                                <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                                    <Icons.BettorEdgeIcon size={18} color={Colors.text.h1} />
                                    <View transparent style={{ flex:1, marginLeft:10 }}>
                                        <Text theme='light'>Powered By</Text>
                                        <Text theme='h1' style={{ marginTop:3 }}>BettorEdge</Text>
                                    </View>
                                    <Button
                                        title='Start Now!'
                                        type='success'
                                        onPress={() => onRequestAuthenticate()}
                                    />
                                </View>
                            </>
                            }
                        </View>
                    </View>    
                    </TouchableWithoutFeedback>            
            </View>
            </TouchableWithoutFeedback>
        </Modal>
    )
}

const styles = StyleSheet.create({
    modalContainer: { flex:1 },
    viewContainer: { flex:1, justifyContent:'flex-end' },
    desktopContainer:{ flex:1, justifyContent:'center', alignItems:'center' }
})

export default OrderHolder